import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, X, XCircle } from 'lucide-react';
import { cx } from '../cx.js';

/**
 * Transient confirmation, announced politely.
 *
 * `aria-live="polite"` rather than `assertive`: a toast says "enregistré",
 * which is worth hearing at the next pause and never worth interrupting a
 * sentence for. Anything that genuinely must interrupt is an Alert with
 * `role="alert"`, in the page, where it stays.
 *
 * Deliberately not used for errors that need a decision. A message that
 * disappears after four seconds is the wrong place for "votre agenda est
 * injoignable" — see §46.
 */

export interface ToastMessage {
  readonly id: number;
  readonly kind: 'success' | 'error';
  readonly text: string;
}

const ToastContext = createContext<((kind: ToastMessage['kind'], text: string) => void) | null>(
  null,
);

export function useToast(): (kind: ToastMessage['kind'], text: string) => void {
  const push = useContext(ToastContext);
  if (push === null) throw new Error('useToast used outside ToastProvider');
  return push;
}

export function ToastProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [messages, setMessages] = useState<readonly ToastMessage[]>([]);

  const push = useCallback((kind: ToastMessage['kind'], text: string): void => {
    const id = Date.now() + Math.random();
    setMessages((current) => [...current, { id, kind, text }]);
    window.setTimeout(() => {
      setMessages((current) => current.filter((message) => message.id !== id));
    }, 5_000);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={cx(
              'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-card border bg-surface px-4 py-3 shadow-overlay',
              message.kind === 'success' ? 'border-success' : 'border-danger',
            )}
          >
            {message.kind === 'success' ? (
              <CheckCircle2
                size={18}
                strokeWidth={1.75}
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-success"
              />
            ) : (
              <XCircle
                size={18}
                strokeWidth={1.75}
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-danger"
              />
            )}
            <p className="min-w-0 flex-1 text-ui-sm text-ink">{message.text}</p>
            <button
              type="button"
              onClick={() => {
                setMessages((current) => current.filter((item) => item.id !== message.id));
              }}
              className="-m-1 rounded-control p-1 text-ink-muted hover:text-ink"
            >
              <X size={16} strokeWidth={1.75} aria-hidden="true" />
              <span className="sr-only">Fermer</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
